import type * as React from "react";
import { Fragment } from "react";
import { Loader } from "@/components/ui/atoms/loader";
import { Skeleton } from "@/components/ui/atoms/skeleton";
import { Message, MessageContent } from "@/components/ui/molecules/message";
import { cn } from "@/lib/utils";

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Framework-agnostic message shape. Normalize messages from any chat SDK
 * (AI SDK, LangChain, Mastra, TanStack AI, etc.) into this format.
 */
export type ChatMessageItem = {
  /** Unique identifier for the message */
  id: string;
  /** Role of the message sender */
  role: "user" | "assistant" | "system";
  /** Text content of the message */
  content: string;
};

export type ChatMessageListProps = {
  /** Messages to render in convenience mode. Not needed when using children. */
  messages?: ChatMessageItem[];
  /** Whether the assistant is currently streaming a response */
  isStreaming?: boolean;
  /** Custom render function for individual messages. Overrides default rendering. */
  renderMessage?: (
    message: ChatMessageItem,
    context: { isLast: boolean; isStreaming: boolean }
  ) => React.ReactNode;
  /** Custom loading indicator shown while waiting for assistant content */
  loadingIndicator?: React.ReactNode;
} & React.ComponentProps<"div">;

// ─── ChatMessageList ────────────────────────────────────────────────────────

/**
 * Layout container for chat messages. Supports two modes:
 *
 * **Children mode (composable):** Pass children for full control over rendering.
 * Use `ChatUserMessage`, `ChatAssistantMessage`, and `ChatStreamingIndicator`
 * sub-components for pre-styled building blocks.
 *
 * **Messages mode (convenience):** Pass a `messages` array for automatic rendering
 * of simple text-based conversations. Use `renderMessage` to customize.
 */
export function ChatMessageList({
  messages,
  isStreaming = false,
  renderMessage,
  loadingIndicator,
  children,
  className,
  ...props
}: ChatMessageListProps) {
  const containerClass = cn(
    "mx-auto flex w-full max-w-3xl flex-col gap-8 px-6",
    className
  );

  // ── Composable mode: children take precedence ──
  if (children) {
    return (
      <div
        aria-live="polite"
        className={containerClass}
        data-slot="chat-message-list"
        role="log"
        {...props}
      >
        {children}
      </div>
    );
  }

  // ── Convenience mode: render from messages array ──
  if (!messages) {
    return null;
  }

  const lastMessage = messages.at(-1);
  const showLoader =
    isStreaming &&
    (!lastMessage || lastMessage.role !== "assistant" || !lastMessage.content);

  return (
    <div
      aria-live="polite"
      className={containerClass}
      data-slot="chat-message-list"
      role="log"
      {...props}
    >
      {messages.map((message) => {
        const isUser = message.role === "user";
        const isLast = message === lastMessage;

        if (renderMessage) {
          return (
            <Fragment key={message.id}>
              {renderMessage(message, {
                isLast,
                isStreaming: isLast && isStreaming,
              })}
            </Fragment>
          );
        }

        if (!(isUser || message.content)) {
          return null;
        }

        return isUser ? (
          <ChatUserMessage key={message.id}>{message.content}</ChatUserMessage>
        ) : (
          <ChatAssistantMessage
            isAnimating={isLast && isStreaming}
            key={message.id}
          >
            {message.content}
          </ChatAssistantMessage>
        );
      })}
      {showLoader && (
        <ChatStreamingIndicator>{loadingIndicator}</ChatStreamingIndicator>
      )}
    </div>
  );
}

// ─── ChatUserMessage ────────────────────────────────────────────────────────

export type ChatUserMessageProps = Omit<
  React.ComponentProps<"div">,
  "children"
> & {
  children: string;
};

/**
 * Pre-styled user message bubble. Right-aligned with primary-colored background.
 * Accepts only string children — use `ChatAssistantMessage` for rich content.
 */
export function ChatUserMessage({
  children,
  className,
  ...props
}: ChatUserMessageProps) {
  return (
    <Message
      className={cn("ml-auto max-w-2xl", className)}
      data-slot="chat-user-message"
      {...props}
    >
      <MessageContent className="rounded-xl bg-primary px-4 py-2 text-primary-foreground">
        {children}
      </MessageContent>
    </Message>
  );
}

// ─── ChatAssistantMessage ───────────────────────────────────────────────────

export type ChatAssistantMessageProps = {
  /** Whether the message text is being streamed (animates text) */
  isAnimating?: boolean;
} & React.ComponentProps<"div">;

/**
 * Pre-styled assistant message. Pass a string for automatic markdown rendering,
 * or pass React elements for full control over content (tool calls, images, etc.).
 *
 * @example Simple text
 * ```tsx
 * <ChatAssistantMessage isAnimating={isStreaming}>
 *   {message.content}
 * </ChatAssistantMessage>
 * ```
 *
 * @example Complex content
 * ```tsx
 * <ChatAssistantMessage>
 *   <MessageContent markdown>{textPart}</MessageContent>
 *   <ToolCallCard name="search" args={args} />
 *   <ImagePreview src={imageUrl} />
 * </ChatAssistantMessage>
 * ```
 */
export function ChatAssistantMessage({
  children,
  isAnimating,
  className,
  ...props
}: ChatAssistantMessageProps) {
  return (
    <Message
      className={className}
      data-slot="chat-assistant-message"
      {...props}
    >
      {typeof children === "string" ? (
        <MessageContent
          className="prose-sm bg-transparent p-0 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg"
          isAnimating={isAnimating}
          markdown
        >
          {children}
        </MessageContent>
      ) : (
        children
      )}
    </Message>
  );
}

// ─── ChatStreamingIndicator ─────────────────────────────────────────────────

export type ChatStreamingIndicatorProps = React.ComponentProps<"div">;

/**
 * Typing / loading indicator shown while waiting for assistant content.
 * Renders a default typing animation, or pass children for a custom indicator.
 */
export function ChatStreamingIndicator({
  children,
  className,
  ...props
}: ChatStreamingIndicatorProps) {
  return (
    <div
      className={cn("flex items-center gap-2 px-1", className)}
      data-slot="chat-streaming-indicator"
      {...props}
    >
      {children ?? <Loader variant="typing" />}
    </div>
  );
}

// ─── ChatMessageListSkeleton ───────────────────────────────────────────────

const SKELETON_MESSAGES = [
  { role: "user" as const, lines: 1, width: "60%" },
  { role: "assistant" as const, lines: 3, width: "85%" },
  { role: "user" as const, lines: 1, width: "45%" },
  { role: "assistant" as const, lines: 2, width: "75%" },
] as const;

export type ChatMessageListSkeletonProps = React.ComponentProps<"div">;

/**
 * Skeleton placeholder that mirrors the `ChatMessageList` layout.
 * Shows alternating user/assistant message bubbles as animated placeholders.
 */
export function ChatMessageListSkeleton({
  className,
  ...props
}: ChatMessageListSkeletonProps) {
  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-3xl flex-col gap-8 px-6",
        className
      )}
      {...props}
    >
      {SKELETON_MESSAGES.map((msg, i) =>
        msg.role === "user" ? (
          <div
            className="ml-auto flex max-w-2xl items-start gap-3"
            key={`msg-skeleton-${i.toString()}`}
          >
            <Skeleton
              className="h-10 rounded-xl"
              style={{ width: msg.width }}
            />
          </div>
        ) : (
          <div
            className="flex flex-col gap-2"
            key={`msg-skeleton-${i.toString()}`}
            style={{ maxWidth: msg.width }}
          >
            {Array.from({ length: msg.lines }).map((_, j) => (
              <Skeleton
                className="h-4 rounded"
                key={`line-${i.toString()}-${j.toString()}`}
                style={{
                  width: j === msg.lines - 1 ? "65%" : "100%",
                }}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}
